<template>
  <PopDialog2
    :title="device.value?.type == 6 ? '블라인드 모터 주소 설정' : '커튼 모터 주소 설정'"
    :visible="props.visible"
    :okCallback="onOkClick"
    :cancelCallback="onCancelClick"
  >
    <div class="md-layout">
      <div class="md-layout-item md-size-50 mx-auto text-center mb-4">
        <!-- <h2 class="title mt-4">{{props.roomName}} 보일러</h2> -->
        <h2 class="title mt-4 mb-4">{{ device?.type == 6 ? '블라인드' : '커튼' }} 모터 주소</h2>
        <div class="row" role="group" style="margin-left: 50px; margin-right: 50px;">
          <button type="button" class="btn btn-light col" 
            style="padding:0px; margin: 0px; max-width: 40px; border-radius: 5px 0px 0px 5px;"
            @click="onAddressDownClick"
          >
            -
          </button>
          <div class="col" role="group" style="padding:0px; margin: 0px; width: 40%;">
            <label class="form-label fw-normal" style="margin: 0px; padding: 0px; border: 1px solid #ebeef4; align-content: center; width: 100%; height: 100%;">
              {{ currentAddress }}
            </label>
          </div>
          <button type="button" class="btn btn-light col" 
            style="max-width: 40px; border-radius: 0px 5px 5px 0px;"
            @click="onAddressUpClick"
          >
            +
          </button>
          <button type="button" class="btn btn-primary col ms-2" 
            style="max-width: 60px;"
            @click="onApplyClick"
          >
            적용
          </button>
        </div>
        <div v-if="device?.type == 5" class="text-muted mt-3" style="font-size: 0.85rem; line-height: 1.3; margin-left: 40px; margin-right: 40px;">
          전동모터의 셋팅 버튼을 길게 눌러 모터 LED가 세번 깜빡일 때 까지 눌러 주세요.
        </div>
        <div v-else-if="device?.type == 6" class="text-muted mt-3" style="font-size: 0.85rem; line-height: 1.3; margin-left: 40px; margin-right: 40px;">
          전동블라인더 셋팅 버튼을 3번 연속으로 눌러 주세요.
        </div>
      </div>
    </div>
  </PopDialog2>
</template>

<script setup>
import { ref, defineProps, computed, getCurrentInstance, reactive, watch } from 'vue';
import { useStore } from 'vuex';
import { ButtonConfig, MQTTCurtainCommand, MQTTDooyaRonoCommand, MQTTDooyaRonoAction } from '@/common/constants';
import useMqttControl from '@/common/hooks/useMqttControl';
import PopDialog2 from '@/common/components/popup/PopDialog2.vue';
import Slider from '@/common/components/widgets/Slider.vue';
import ListGroupItem from '@/common/components/widgets/ListGroupItem.vue';
import Axios, { DeviceApi } from '@/api';

const mqttControl = reactive(useMqttControl());
const { emitter } = getCurrentInstance().appContext.config.globalProperties;
const store = useStore();
const props = defineProps([
  'visible',
  'okCallback',
  'cancelCallback',
  'roomName',
  'roomId',
  'deviceId',
  'handlePowerClick'
]);
const timerId = ref(null);

const zone = computed(() => {
  if (store.state.home.zones.length > 0) {
    return store.getters['home/getCurrentZone'];
  } else return null;
});
const group = computed(() => {
  return zone.value.groups.find((g) => g._id === props.roomId);
});
const devices = computed(() => {
  return group.value ? group.value.devices : undefined;
});
const device = computed(() => {
  return devices.value.find(
    (d) => d._id === props.deviceId,
  );
});
const dooyaAddressStatus = computed(() => store.state.home.dooya?.addressStatus);
const ronoAddressStatus = computed(() => store.state.home.rono?.addressStatus);
const currentAddress = ref(parseInt(device.value.item.address));

watch(dooyaAddressStatus, async (next, prev) => {
  if (!timerId.value) return;

  if (prev === 'idle' && next === 'pending') {
    clearTimeout(timerId.value);
    timerId.value = setTimeout(() => {
      timerId.value = null;
      store.commit('home/clearDooya');
      emitter.emit('hideLoading');
      emitter.emit('showPopAlert', {
        description: '모터 주소 설정에 실패했습니다.',
      });
    }, 30000);

    console.log('dooya addressStatus: idle -> pending');
  } else if (prev === 'pending' && next === 'done') {
    await requestUpdateDevice(device.value._id, {
      item: { address: `${currentAddress.value}` }
    });

    clearTimeout(timerId.value);
    timerId.value = null;
    store.commit('home/clearDooya');
    emitter.emit('hideLoading');
    if (props.cancelCallback) {
      props.cancelCallback();
    }
    console.log('dooya addressStatus: pending -> done');
  }
});
watch(ronoAddressStatus, async (next, prev) => {
  if (!timerId.value) return;

  if (prev === 'idle' && next === 'pending') {
    clearTimeout(timerId.value);
    timerId.value = setTimeout(() => {
      timerId.value = null;
      store.commit('home/clearRono');
      emitter.emit('hideLoading');
      emitter.emit('showPopAlert', {
        description: '모터 주소 설정에 실패했습니다.',
      });
    }, 35000);

    console.log('dooya addressStatus: idle -> pending');
  } else if (prev === 'pending' && next === 'done') {
    await requestUpdateDevice(device.value._id, {
      item: { address: `${currentAddress.value}` }
    });

    clearTimeout(timerId.value);
    timerId.value = null;
    store.commit('home/clearDooya');
    emitter.emit('hideLoading');
    if (props.cancelCallback) {
      props.cancelCallback();
    }
    console.log('rono addressStatus: idle -> pending');
  }
});

const onCancelClick = () => {
  if (props.cancelCallback) {
    props.cancelCallback();
  }
};

const onAddressDownClick = (e) => {
  currentAddress.value = currentAddress.value <= 0 ? 0 : currentAddress.value - 1;
}

const onAddressUpClick = (e) => {
  currentAddress.value = currentAddress.value >= 9 ? 9 : currentAddress.value + 1;
}

const requestUpdateDevice = async (id, params) => {
  return new Promise(async (resolve, reject) => {
    try {
      const result = await Axios.patch(DeviceApi.UPDATE_DEVICE.replace(':id', id), params);
      if (result.resultCode === '0000') {
        console.log(result.resultData)
        store.commit('home/updateDevice', {
          groupId: group.value._id,
          id,
          data: params,
        });
        resolve(result);
      } else {
        emitter.emit('showPopAlert', {
          description: result.resultMessage,
        });
        reject(result.resultMessage);
      }
    } catch (error) {
      emitter.emit('showPopAlert', {
        description: '장치 정보 수정에 실패했습니다.',
      });
      reject('장치 정보 수정에 실패했습니다.');
    }
  });
};

const onApplyClick = async () => {
  emitter.emit('showLoading');
  try {
    if (device.value.type == 3) {
      const curtainUniqId = device.value.item.uniqId || String(device.value._id).padEnd(13, "0");
      mqttControl.onPublishTemp(device.value.controllerId, MQTTCurtainCommand.RS485AddrWrite, `${currentAddress.value},${curtainUniqId}`);
    } else if (device.value.type == 5) {
      mqttControl.onPublish(device.value.controllerId, device.value._id, {
        cmd: MQTTDooyaRonoCommand.DooyaAddr,
        type: 1,
        data: {
          address: currentAddress.value,
        }
      });
    } else if (device.value.type == 6) {
      mqttControl.onPublish(device.value.controllerId, device.value._id, {
        cmd: MQTTDooyaRonoCommand.RonoAddr,
        type: 1,
        data: {
          address: currentAddress.value,
        }
      });
    }

    
    if (device.value.type == 3) {
      await requestUpdateDevice(device.value._id, {
        item: { address: `${currentAddress.value}` }
      });
      setTimeout(() => {
        if (props.cancelCallback) {
          props.cancelCallback();
        }
        emitter.emit('hideLoading');
      }, ButtonConfig.delay);
    } else if (device.value.type == 5) {
      timerId.value = setTimeout(() => {
        timerId.value = null;
        store.commit('home/clearDooya');
        emitter.emit('hideLoading');
        emitter.emit('showPopAlert', {
          description: '모터 주소 설정에 실패했습니다.',
        });
      }, 5000);
    } else if (device.value.type == 6) {
      timerId.value = setTimeout(() => {
        timerId.value = null;
        store.commit('home/clearRono');
        emitter.emit('hideLoading');
        emitter.emit('showPopAlert', {
          description: '모터 주소 설정에 실패했습니다.',
        });
      }, 5000);
    }
  } catch(e) {
    emitter.emit('hideLoading');
    console.log(e);
  }
}
</script>
